import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/AuthContext'
import { Phone, PhoneOff, Video as VideoIcon, Users } from 'lucide-react'
import { toast } from 'sonner'
import Avatar from '@/components/Avatar'

/**
 * Globally-mounted modal that listens for incoming-call invites
 * (call_participants rows where user_id = me AND status = 'invited').
 *
 * Mount once near the root, inside <AuthProvider>. Pure UI overlay; renders
 * nothing while no calls are ringing.
 */
export default function IncomingCallModal() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [incoming, setIncoming] = useState(null) // { call, initiator, group? }

  // Fetch the full call + initiator profile so we can render names/avatars.
  const loadCall = async (callId) => {
    const { data: call } = await supabase
      .from('calls')
      .select('*')
      .eq('id', callId)
      .single()
    if (!call) return null
    if (call.status !== 'ringing') return null // already answered / declined elsewhere
    // Suppress stale invites — server-side reaper will mark them missed, but
    // we don't want a 5-minute-old ringing call to pop up just because the
    // user opened the app.
    const ageMs = Date.now() - new Date(call.started_at).getTime()
    if (ageMs > 30_000) {
      supabase.rpc('reap_stale_calls').then(() => {})
      return null
    }
    const { data: initiator } = await supabase
      .from('profiles')
      .select('id, full_name, email, avatar_url')
      .eq('id', call.initiator_id)
      .single()
    let group = null
    if (call.group_id) {
      const { data: g } = await supabase
        .from('groups')
        .select('id, name, avatar_url')
        .eq('id', call.group_id)
        .single()
      group = g
    }
    return { call, initiator, group }
  }

  // 1) On mount, check for any in-flight invites (e.g. user just reloaded the page
  // while a call was ringing).
  useEffect(() => {
    if (!user) return
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('call_participants')
        .select('call_id')
        .eq('user_id', user.id)
        .eq('status', 'invited')
        .order('invited_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (cancelled || !data) return
      const payload = await loadCall(data.call_id)
      if (!cancelled && payload) setIncoming(payload)
    })()
    return () => { cancelled = true }
  }, [user])

  // 2) Realtime: new invites for me → show the modal.
  useEffect(() => {
    if (!user) return
    const channel = supabase
      .channel(`call_invites_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table:  'call_participants',
          filter: `user_id=eq.${user.id}`,
        },
        async (msg) => {
          if (msg.new?.status !== 'invited') return
          const payload = await loadCall(msg.new.call_id)
          if (payload) setIncoming(payload)
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table:  'calls',
        },
        (msg) => {
          // If the ringing call we're showing transitions to ended/declined/missed
          // (e.g. caller hung up), dismiss the modal.
          if (incoming && msg.new?.id === incoming.call.id && msg.new?.status !== 'ringing') {
            setIncoming(null)
          }
        },
      )
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [user, incoming])

  if (!incoming) return null

  const { call, initiator, group } = incoming

  const accept = async () => {
    const { error } = await supabase.rpc('answer_call', { p_call_id: call.id })
    if (error) { toast.error(error.message); return }
    setIncoming(null)
    queryClient.invalidateQueries({ queryKey: ['call_history', user.id] })
    navigate(`/call/${call.id}`)
  }
  const decline = async () => {
    const { error } = await supabase.rpc('decline_call', { p_call_id: call.id })
    if (error) toast.error(error.message)
    setIncoming(null)
    queryClient.invalidateQueries({ queryKey: ['call_history', user.id] })
  }

  const callerLabel = group?.name
    ? `${initiator?.full_name || initiator?.email?.split('@')[0] || 'Someone'} · ${group.name}`
    : initiator?.full_name || initiator?.email?.split('@')[0] || 'Someone'

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-md animate-fade-in">
      <div className="clay-card max-w-sm w-[90%] p-6 text-center space-y-5">
        <div className="flex justify-center">
          <div className="relative">
            <Avatar
              profile={group ? { full_name: group.name, avatar_url: group.avatar_url } : initiator}
              size="w-24 h-24"
              textSize="text-2xl"
              bordered
            />
            <div className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center shadow-md ring-4 ring-white">
              {call.kind === 'video' ? <VideoIcon size={18} /> : <Phone size={18} />}
            </div>
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-green-600 flex items-center gap-1.5 justify-center">
            {group ? <Users size={12} /> : null}
            Incoming {call.kind === 'video' ? 'video' : 'voice'} call
          </p>
          <p className="text-xl font-bold text-gray-800 mt-1">{callerLabel}</p>
        </div>
        <div className="flex justify-center gap-6 pt-2">
          <button
            onClick={decline}
            aria-label="Decline call"
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg"
          >
            <PhoneOff size={26} />
          </button>
          <button
            onClick={accept}
            aria-label="Answer call"
            className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center shadow-lg animate-pulse"
          >
            {call.kind === 'video' ? <VideoIcon size={26} /> : <Phone size={26} />}
          </button>
        </div>
      </div>
    </div>
  )
}
